import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  Assessment as ReportIcon,
  TrendingUp as ProfitIcon,
  AccountBalance as DrawerIcon,
  Receipt as ExpenseIcon,
  ShoppingCart as SalesIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Build as RepairIcon
} from '@mui/icons-material';
import { formatCurrency } from '../utils/numberUtils';
import {
  generateMonthlyReport,
  generateSimpleMonthlyReport,
  exportMonthlyReportToPDF,
  exportMonthlyReportToExcel,
  MonthlyReport
} from '../services/monthlyReportService';

const MonthlyReportPage: React.FC = () => {
  
  // حالة التقرير
  const [report, setReport] = useState<MonthlyReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // حالة اختيار الشهر والسنة
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  
  // حالة التصدير
  const [exporting, setExporting] = useState(false);

  // حالة نوع التقرير (للاختبار)
  const [useSimpleReport, setUseSimpleReport] = useState(false);

  // تحميل التقرير عند تغيير الشهر أو السنة
  useEffect(() => {
    loadMonthlyReport();
  }, [selectedYear, selectedMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  // تحميل التقرير الشهري
  const loadMonthlyReport = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log(`📊 بدء تحميل التقرير الشهري لشهر ${selectedMonth}/${selectedYear}`);

      // اختبار بسيط أولاً
      if (!selectedYear || !selectedMonth) {
        throw new Error('يجب اختيار الشهر والسنة');
      }

      // محاولة النسخة الكاملة مع معالجة أفضل للأخطاء
      try {
        console.log('🔄 محاولة التقرير الكامل...');
        const monthlyReport = await generateMonthlyReport(selectedYear, selectedMonth);
        console.log('✅ نجح التقرير الكامل:', monthlyReport);
        setReport(monthlyReport);
      } catch (fullReportError) {
        console.warn('⚠️ فشل التقرير الكامل، استخدام النسخة المبسطة:', fullReportError);
        const simpleReport = await generateSimpleMonthlyReport(selectedYear, selectedMonth);
        console.log('✅ نجح التقرير المبسط:', simpleReport);
        setReport(simpleReport);
      }

    } catch (error: any) {
      console.error('❌ خطأ في تحميل التقرير الشهري:', error);
      const errorMessage = error?.message || 'فشل في تحميل التقرير الشهري';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // تصدير إلى PDF
  const handleExportToPDF = async () => {
    if (!report) return;
    
    try {
      setExporting(true);
      await exportMonthlyReportToPDF(report);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      setError('فشل في تصدير التقرير إلى PDF');
    } finally {
      setExporting(false);
    }
  };

  // تصدير إلى Excel
  const handleExportToExcel = async () => {
    if (!report) return;
    
    try {
      setExporting(true);
      await exportMonthlyReportToExcel(report);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setError('فشل في تصدير التقرير إلى Excel');
    } finally {
      setExporting(false);
    }
  };

  // إنشاء قائمة السنوات (السنة الحالية + 5 سنوات سابقة)
  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);
  
  // أسماء الشهور
  const months = [
    { value: 1, label: 'يناير' },
    { value: 2, label: 'فبراير' },
    { value: 3, label: 'مارس' },
    { value: 4, label: 'أبريل' },
    { value: 5, label: 'مايو' },
    { value: 6, label: 'يونيو' },
    { value: 7, label: 'يوليو' },
    { value: 8, label: 'أغسطس' },
    { value: 9, label: 'سبتمبر' },
    { value: 10, label: 'أكتوبر' },
    { value: 11, label: 'نوفمبر' },
    { value: 12, label: 'ديسمبر' }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* العنوان وأدوات التحكم */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center">
            <ReportIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                التقرير الشهري الشامل
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                تقرير مفصل للمكسب والخسارة الشهرية
              </Typography>
            </Box>
          </Box>
          
          {/* أزرار التصدير */}
          {report && (
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<PdfIcon />}
                onClick={handleExportToPDF}
                disabled={exporting}
              >
                PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={<ExcelIcon />}
                onClick={handleExportToExcel}
                disabled={exporting}
              >
                Excel
              </Button>
            </Box>
          )}
        </Box>

        {/* اختيار الشهر والسنة */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>السنة</InputLabel>
              <Select
                value={selectedYear}
                label="السنة"
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {years.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>الشهر</InputLabel>
              <Select
                value={selectedMonth}
                label="الشهر"
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
              >
                {months.map(month => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              onClick={loadMonthlyReport}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <ReportIcon />}
              fullWidth
            >
              {loading ? 'جاري التحميل...' : 'تحديث التقرير'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* رسائل الخطأ */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* محتوى التقرير */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={5}>
          <CircularProgress size={60} />
        </Box>
      ) : report ? (
        <Box>
          {/* ملخص التقرير النهائي */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom display="flex" alignItems="center">
              <ProfitIcon sx={{ mr: 1, color: 'primary.main' }} />
              ملخص المكسب والخسارة - {report.month} {report.year}
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: 'success.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                    صافي ربح الصيانة
                    </Typography>
                    <Typography variant="h4">
                      {formatCurrency(report.finalProfitLoss.totalMaintenanceProfit)}
                    </Typography>
                    <Typography variant="caption">
                      بعد خصم تكلفة القطع فقط (مؤقتاً)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: 'error.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      إجمالي المصاريف
                    </Typography>
                    <Typography variant="h4">
                      {formatCurrency(report.finalProfitLoss.totalBasicExpenses + report.finalProfitLoss.totalDailyExpenses)}
                    </Typography>
                    <Typography variant="caption">
                      أساسية + يومية
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: 'info.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      ربح المبيعات
                    </Typography>
                    <Typography variant="h4">
                      {formatCurrency(report.finalProfitLoss.totalSalesPartsProfit)}
                    </Typography>
                    <Typography variant="caption">
                      فرق سعر قطع الغيار
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Card sx={{ 
                  bgcolor: report.finalProfitLoss.netMonthlyProfit >= 0 ? 'success.main' : 'error.main',
                  color: 'white'
                }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      صافي الربح الشهري
                    </Typography>
                    <Typography variant="h4">
                      {formatCurrency(report.finalProfitLoss.netMonthlyProfit)}
                    </Typography>
                    <Typography variant="caption">
                      هامش الربح: {report.finalProfitLoss.profitMargin.toFixed(2)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            {/* معادلة الحساب الجديدة الجديدة */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                📊 معادلة الحساب الجديدة:
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>صافي الربح الشهري</strong> = 
                (صافي ربح الصيانة) - (المصاريف الأساسية + المصاريف اليومية) + (ربح المبيعات)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                = {formatCurrency(report.finalProfitLoss.totalMaintenanceProfit)} - 
                ({formatCurrency(report.finalProfitLoss.totalBasicExpenses)} + 
                {formatCurrency(report.finalProfitLoss.totalDailyExpenses)}) + 
                {formatCurrency(report.finalProfitLoss.totalSalesPartsProfit)} = 
                <strong>{formatCurrency(report.finalProfitLoss.netMonthlyProfit)}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                <strong>💡 ملاحظة:</strong> صافي ربح الصيانة = (أجر الصيانة - تكلفة قطع الغيار - نسب الفنيين)
                <br />
                <strong>🔗 مصادر البيانات:</strong> الأرباح من صفحة التقارير، المصروفات من صفحة المصروفات
              </Typography>
            </Box>
          </Paper>

          {/* تقرير ربح الصيانة المفصل */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom display="flex" alignItems="center">
              <RepairIcon sx={{ mr: 1, color: 'primary.main' }} />
              تحليل ربح الصيانة المفصل
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary.main">
                      💰 الإيرادات والتكاليف
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        إجمالي إيرادات الصيانة:
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {formatCurrency(report.maintenanceProfit?.totalMaintenanceRevenue || 0)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        تكلفة قطع الغيار (شراء):
                      </Typography>
                      <Typography variant="h6" color="error.main">
                        -{formatCurrency(report.maintenanceProfit?.totalMaintenancePartsCost || 0)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        نسب الفنيين:
                      </Typography>
                      <Typography variant="h6" color="warning.main">
                        -{formatCurrency(report.maintenanceProfit?.totalTechnicianFees || 0)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary.main">
                      📊 النتائج النهائية
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        أجر الصيانة (بدون قطع):
                      </Typography>
                      <Typography variant="h6" color="info.main">
                        {formatCurrency(report.maintenanceProfit?.totalMaintenanceServiceFees || 0)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                      <Typography variant="body1" gutterBottom>
                        <strong>صافي ربح الصيانة:</strong>
                      </Typography>
                      <Typography variant="h4" color="success.main" fontWeight="bold">
                        {formatCurrency(report.maintenanceProfit?.netMaintenanceProfit || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        = أجر الصيانة - تكلفة القطع - نسب الفنيين
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>

          {/* تقرير ربح الصيانة المفصل */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom display="flex" alignItems="center">
              <RepairIcon sx={{ mr: 1, color: 'primary.main' }} />
              تحليل ربح الصيانة المفصل
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary.main">
                      💰 الإيرادات والتكاليف
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        إجمالي إيرادات الصيانة:
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {formatCurrency(report.maintenanceProfit?.totalMaintenanceRevenue || 0)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        تكلفة قطع الغيار (شراء):
                      </Typography>
                      <Typography variant="h6" color="error.main">
                        -{formatCurrency(report.maintenanceProfit?.totalMaintenancePartsCost || 0)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        نسب الفنيين:
                      </Typography>
                      <Typography variant="h6" color="warning.main">
                        -{formatCurrency(report.maintenanceProfit?.totalTechnicianFees || 0)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary.main">
                      📊 النتائج النهائية
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        أجر الصيانة (بدون قطع):
                      </Typography>
                      <Typography variant="h6" color="info.main">
                        {formatCurrency(report.maintenanceProfit?.totalMaintenanceServiceFees || 0)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                      <Typography variant="body1" gutterBottom>
                        <strong>صافي ربح الصيانة:</strong>
                      </Typography>
                      <Typography variant="h4" color="success.main" fontWeight="bold">
                        {formatCurrency(report.maintenanceProfit?.netMaintenanceProfit || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        = أجر الصيانة - تكلفة القطع - نسب الفنيين
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>

          {/* تقرير الدرج اليومي */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom display="flex" alignItems="center">
              <DrawerIcon sx={{ mr: 1, color: 'primary.main' }} />
              تقرير الدرج اليومي
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              💡 كل يوم يتم خصم مصاريفه اليومية من إيراداته فقط
            </Typography>

            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>التاريخ</TableCell>
                    <TableCell align="right">دخل المبيعات</TableCell>
                    <TableCell align="right">دخل الصيانة</TableCell>
                    <TableCell align="right">المصاريف اليومية</TableCell>
                    <TableCell align="right">صافي اليوم</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.dailyDrawerSummary.map((day) => (
                    <TableRow key={day.date}>
                      <TableCell>{day.date}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>
                        {formatCurrency(day.salesIncome)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'info.main' }}>
                        {formatCurrency(day.maintenanceIncome)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>
                        {formatCurrency(day.dailyExpenses)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: day.netDailyProfit >= 0 ? 'success.main' : 'error.main',
                          fontWeight: 'bold'
                        }}
                      >
                        {formatCurrency(day.netDailyProfit)}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* إجمالي الشهر */}
                  <TableRow sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>إجمالي الشهر</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                      {formatCurrency(report.dailyDrawerSummary.reduce((sum, day) => sum + day.salesIncome, 0))}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'info.main', fontWeight: 'bold' }}>
                      {formatCurrency(report.dailyDrawerSummary.reduce((sum, day) => sum + day.maintenanceIncome, 0))}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                      {formatCurrency(report.dailyDrawerSummary.reduce((sum, day) => sum + day.dailyExpenses, 0))}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                      {formatCurrency(report.totalDrawerProfit)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* المصاريف الشهرية */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom display="flex" alignItems="center">
              <ExpenseIcon sx={{ mr: 1, color: 'error.main' }} />
              المصاريف الشهرية (مطابقة لصفحة المصروفات)
            </Typography>
            
            {/* عرض تقسيم المصاريف */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: 'error.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h6">المصاريف الأساسية</Typography>
                    <Typography variant="h4">
                      {formatCurrency(report.monthlyExpenses.totalBasicExpenses)}
                    </Typography>
                    <Typography variant="caption">شهرية</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: 'warning.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h6">المصاريف اليومية</Typography>
                    <Typography variant="h4">
                      {formatCurrency(report.monthlyExpenses.totalDailyExpenses || 0)}
                    </Typography>
                    <Typography variant="caption">للشهر الكامل</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: 'error.dark', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h6">إجمالي المصاريف</Typography>
                    <Typography variant="h4">
                      {formatCurrency(report.monthlyExpenses.totalBasicExpenses + (report.monthlyExpenses.totalDailyExpenses || 0))}
                    </Typography>
                    <Typography variant="caption">أساسية + يومية</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  المصاريف حسب التصنيف
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>التصنيف</TableCell>
                        <TableCell align="right">المبلغ</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(report.monthlyExpenses.expensesByCategory).map(([category, amount]) => (
                        <TableRow key={category}>
                          <TableCell>{category}</TableCell>
                          <TableCell align="right" sx={{ color: 'error.main' }}>
                            {formatCurrency(amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>الإجمالي</TableCell>
                        <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                          {formatCurrency(report.monthlyExpenses.totalBasicExpenses)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  تفاصيل المصاريف
                </Typography>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>التاريخ</TableCell>
                        <TableCell>الوصف</TableCell>
                        <TableCell align="right">المبلغ</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.monthlyExpenses.expenseDetails.map((expense, index) => (
                        <TableRow key={index}>
                          <TableCell>{expense.date.toLocaleDateString()}</TableCell>
                          <TableCell>{expense.description}</TableCell>
                          <TableCell align="right" sx={{ color: 'error.main' }}>
                            {formatCurrency(expense.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Paper>

          {/* ربح المبيعات */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom display="flex" alignItems="center">
              <SalesIcon sx={{ mr: 1, color: 'info.main' }} />
              ربح المبيعات (فرق أسعار قطع الغيار)
            </Typography>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="primary">
                      إجمالي الإيرادات
                    </Typography>
                    <Typography variant="h4">
                      {formatCurrency(report.salesPartsProfit.totalSalesRevenue)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="error">
                      تكلفة قطع الغيار
                    </Typography>
                    <Typography variant="h4">
                      {formatCurrency(report.salesPartsProfit.totalPartsCost)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="success">
                      صافي الربح
                    </Typography>
                    <Typography variant="h4">
                      {formatCurrency(report.salesPartsProfit.totalPartsProfit)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="info">
                      هامش الربح
                    </Typography>
                    <Typography variant="h4">
                      {report.salesPartsProfit.profitMargin.toFixed(1)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* ربح المبيعات حسب التصنيف */}
            <Typography variant="h6" gutterBottom>
              ربح المبيعات حسب التصنيف
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>التصنيف</TableCell>
                    <TableCell align="right">الكمية</TableCell>
                    <TableCell align="right">الإيرادات</TableCell>
                    <TableCell align="right">التكلفة</TableCell>
                    <TableCell align="right">الربح</TableCell>
                    <TableCell align="right">هامش الربح</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(report.salesPartsProfit.salesByCategory).map(([category, data]) => (
                    <TableRow key={category}>
                      <TableCell>{category}</TableCell>
                      <TableCell align="right">{data.quantity}</TableCell>
                      <TableCell align="right" sx={{ color: 'primary.main' }}>
                        {formatCurrency(data.revenue)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>
                        {formatCurrency(data.cost)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>
                        {formatCurrency(data.profit)}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${data.revenue > 0 ? ((data.profit / data.revenue) * 100).toFixed(1) : 0}%`}
                          color={data.profit > 0 ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      ) : (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            اختر الشهر والسنة لعرض التقرير
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default MonthlyReportPage;
